import { FUND_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX } from 'pages/Funds/common/constants/fundSummary';
import {
  generateColumnKey,
  getBooleanValue,
  getNumberOrNAValue,
  getNumberValue,
  getStringValue,
  shortDate,
} from 'utilities';
import { CreateColumns, FundSummaryColumn } from './types';

const createColumns: CreateColumns = params => {
  const { companies, fundInvestments, isProcessManagementEnabled } = params;

  // Process Management
  const companiesProcessManagement = companies.reduce(
    (accumulator, current) => ({
      ...accumulator,
      [generateColumnKey({
        id: current?.cap_table,
        name: current?.company_slug,
        prefix: FUND_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      })]: {
        name: getStringValue(current?.company_name),
        slug: getStringValue(current?.company_slug),
      },
    }),
    {}
  ) as FundSummaryColumn;

  // Investment Date
  const companiesInvestmentDate = companies.reduce(
    (accumulator, current) => ({
      ...accumulator,
      [generateColumnKey({
        id: current?.cap_table,
        name: current?.company_slug,
        prefix: FUND_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      })]: {
        value: getStringValue(shortDate(current?.initial_investment_date)),
      },
    }),
    {}
  ) as FundSummaryColumn;

  // Most Recent Investment Date
  const companiesMostRecentInvestmentDate = companies.reduce(
    (accumulator, current) => ({
      ...accumulator,
      [generateColumnKey({
        id: current?.cap_table,
        name: current?.company_slug,
        prefix: FUND_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      })]: {
        value: getStringValue(shortDate(current?.most_recent_investment_date)),
      },
    }),
    {}
  ) as FundSummaryColumn;

  // Fully Diluted Ownership %
  const companiesFullyDilutedOwnership = companies.reduce((accumulator, current) => {
    const hasOnlyCustomOwnerships = getBooleanValue(current?.has_only_custom_ownerships);

    return {
      ...accumulator,
      [generateColumnKey({
        id: current?.cap_table,
        name: current?.company_slug,
        prefix: FUND_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      })]: {
        disabled: hasOnlyCustomOwnerships,
        value: hasOnlyCustomOwnerships ? 0 : getNumberValue(current?.diluted_ownership),
      },
    };
  }, {}) as FundSummaryColumn;

  // Invested Capital
  const companiesInvestedCapital = companies.reduce(
    (accumulator, current) => ({
      ...accumulator,
      [generateColumnKey({
        id: current?.cap_table,
        name: current?.company_slug,
        prefix: FUND_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      })]: {
        disabled: !current?.is_published,
        value: getNumberValue(current?.fund_cost_basis),
      },
    }),
    {}
  ) as FundSummaryColumn;

  // Realized Value
  const companiesRealizedValue = companies.reduce(
    (accumulator, current) => ({
      ...accumulator,
      [generateColumnKey({
        id: current?.cap_table,
        name: current?.company_slug,
        prefix: FUND_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      })]: {
        disabled: !current?.is_published,
        value: getNumberValue(current?.realized_value),
      },
    }),
    {}
  ) as FundSummaryColumn;

  // Unrealized Equity
  const companiesUnrealizedEquity = companies.reduce(
    (accumulator, current) => ({
      ...accumulator,
      [generateColumnKey({
        id: current?.cap_table,
        name: current?.company_slug,
        prefix: FUND_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      })]: {
        disabled: !current?.is_published,
        value: getNumberValue(current?.unrealized_value),
      },
    }),
    {}
  ) as FundSummaryColumn;

  // Total Value
  const companiesTotalValue = companies.reduce(
    (accumulator, current) => ({
      ...accumulator,
      [generateColumnKey({
        id: current?.cap_table,
        name: current?.company_slug,
        prefix: FUND_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      })]: {
        disabled: !current?.is_published,
        value: getNumberValue(current?.total_value),
      },
    }),
    {}
  ) as FundSummaryColumn;

  // Gross IRR
  const companiesGrossIRR = companies.reduce(
    (accumulator, current) => ({
      ...accumulator,
      [generateColumnKey({
        id: current?.cap_table,
        name: current?.company_slug,
        prefix: FUND_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      })]: {
        disabled: !current?.is_published,
        value: getNumberOrNAValue(current?.gross_irr),
      },
    }),
    {}
  ) as FundSummaryColumn;

  // MOIC
  const companiesMOIC = companies.reduce((accumulator, current) => {
    const companyInvestedCapital = getNumberValue(current?.fund_cost_basis);
    const companyTotalValue = getNumberValue(current?.total_value);

    return {
      ...accumulator,
      [generateColumnKey({
        id: current?.cap_table,
        name: current?.company_slug,
        prefix: FUND_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      })]: {
        disabled: !current?.is_published,
        value: companyInvestedCapital > 0 ? companyTotalValue / companyInvestedCapital : 0,
      },
    };
  }, {}) as FundSummaryColumn;

  // Fund investments - process management
  const fundInvestmentsProcessManagement = fundInvestments.reduce(
    (accumulator, current) => ({
      ...accumulator,
      [generateColumnKey({
        id: current?.id,
        name: current?.company_slug,
        prefix: FUND_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      })]: {
        isFundInvestment: true,
        name: getStringValue(current?.company_name),
        slug: getStringValue(current?.company_slug),
      },
    }),
    {}
  ) as FundSummaryColumn;

  // Fund investments - investment date
  const fundInvestmentsInvestmentDate = fundInvestments.reduce(
    (accumulator, current) => ({
      ...accumulator,
      [generateColumnKey({
        id: current?.id,
        name: current?.company_slug,
        prefix: FUND_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      })]: {
        isFundInvestment: true,
        value: null,
      },
    }),
    {}
  ) as FundSummaryColumn;

  // Fund investments - most recent investment date
  const fundInvestmentsMostRecentInvestmentDate = fundInvestments.reduce(
    (accumulator, current) => ({
      ...accumulator,
      [generateColumnKey({
        id: current?.id,
        name: current?.company_slug,
        prefix: FUND_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      })]: {
        isFundInvestment: true,
        value: null,
      },
    }),
    {}
  ) as FundSummaryColumn;

  // Fund investments - fully diluted ownership %
  const fundInvestmentsFullyDilutedOwnership = fundInvestments.reduce(
    (accumulator, current) => ({
      ...accumulator,
      [generateColumnKey({
        id: current?.id,
        name: current?.company_slug,
        prefix: FUND_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      })]: {
        isFundInvestment: true,
        value: null,
      },
    }),
    {}
  ) as FundSummaryColumn;

  // Fund investments - invested capital
  const fundInvestmentsInvestedCapital = fundInvestments.reduce(
    (accumulator, current) => ({
      ...accumulator,
      [generateColumnKey({
        id: current?.id,
        name: current?.company_slug,
        prefix: FUND_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      })]: {
        disabled: !current?.is_published,
        isFundInvestment: true,
        value: getNumberValue(current?.invested_capital) * Number(current?.exchange_rate_to_fund_currency ?? 1),
      },
    }),
    {}
  ) as FundSummaryColumn;

  // Fund investments - realized value
  const fundInvestmentsRealizedValue = fundInvestments.reduce(
    (accumulator, current) => ({
      ...accumulator,
      [generateColumnKey({
        id: current?.id,
        name: current?.company_slug,
        prefix: FUND_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      })]: {
        disabled: !current?.is_published,
        isFundInvestment: true,
        value: null,
      },
    }),
    {}
  ) as FundSummaryColumn;

  // Fund investments - unrealized equity
  const fundInvestmentsUnrealizedEquity = fundInvestments.reduce(
    (accumulator, current) => ({
      ...accumulator,
      [generateColumnKey({
        id: current?.id,
        name: current?.company_slug,
        prefix: FUND_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      })]: {
        disabled: !current?.is_published,
        isFundInvestment: true,
        value: getNumberValue(current?.unrealized_value) * Number(current?.exchange_rate_to_fund_currency ?? 1),
      },
    }),
    {}
  ) as FundSummaryColumn;

  // Fund investments - total value
  const fundInvestmentsTotalValue = fundInvestments.reduce(
    (accumulator, current) => ({
      ...accumulator,
      [generateColumnKey({
        id: current?.id,
        name: current?.company_slug,
        prefix: FUND_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      })]: {
        disabled: !current?.is_published,
        isFundInvestment: true,
        value: null,
      },
    }),
    {}
  ) as FundSummaryColumn;

  // Fund investments - gross IRR
  const fundInvestmentsGrossIRR = fundInvestments.reduce(
    (accumulator, current) => ({
      ...accumulator,
      [generateColumnKey({
        id: current?.id,
        name: current?.company_slug,
        prefix: FUND_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      })]: {
        disabled: !current?.is_published,
        isFundInvestment: true,
        value: null,
      },
    }),
    {}
  ) as FundSummaryColumn;

  // Fund investments - MOIC
  const fundInvestmentsMOIC = fundInvestments.reduce(
    (accumulator, current) => ({
      ...accumulator,
      [generateColumnKey({
        id: current?.id,
        name: current?.company_slug,
        prefix: FUND_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      })]: {
        disabled: !current?.is_published,
        isFundInvestment: true,
        value: null,
      },
    }),
    {}
  ) as FundSummaryColumn;

  const investmentDate = { ...companiesInvestmentDate, ...fundInvestmentsInvestmentDate };
  const mostRecentInvestmentDate = { ...companiesMostRecentInvestmentDate, ...fundInvestmentsMostRecentInvestmentDate };
  const fullyDilutedOwnership = { ...companiesFullyDilutedOwnership, ...fundInvestmentsFullyDilutedOwnership };
  const investedCapital = { ...companiesInvestedCapital, ...fundInvestmentsInvestedCapital };
  const realizedValue = { ...companiesRealizedValue, ...fundInvestmentsRealizedValue };
  const unrealizedEquity = { ...companiesUnrealizedEquity, ...fundInvestmentsUnrealizedEquity };
  const totalValue = { ...companiesTotalValue, ...fundInvestmentsTotalValue };
  const grossIRR = { ...companiesGrossIRR, ...fundInvestmentsGrossIRR };
  const MOIC = { ...companiesMOIC, ...fundInvestmentsMOIC };

  const columns = [
    investmentDate,
    mostRecentInvestmentDate,
    fullyDilutedOwnership,
    investedCapital,
    realizedValue,
    unrealizedEquity,
    totalValue,
    grossIRR,
    MOIC,
  ];

  const processManagement = { ...companiesProcessManagement, ...fundInvestmentsProcessManagement };

  return isProcessManagementEnabled ? [processManagement, ...columns] : columns;
};

export default createColumns;
